import React from "react"
import styled, { css } from "styled-components"
import colors from "@constants/colors"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import { smallcapsBold13Styles } from "@constants/typography"
import { StyledButton } from "@components/buttons/Button"
import TextInput from "@components/inputs/TextInput"
import BackButton from "./BackButton"
import { DiscountResponseProps } from "./types"

const openProps = css`
  transform: translateX(0);
  transition: transform ${animation.medium} ${cubicBezier};
`

const Container = styled.div<{ isOpen?: boolean }>`
  z-index: 3;
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  opacity: 1;
  -webkit-overflow-scrolling: touch;
  transform: translateX(100%);
  transition: transform ${animation.medium} ${cubicBezier};
  will-change: transform;
  background-color: ${colors.offWhite};
  overflow: hidden;
  overflow-y: auto;
  cursor: default;

  ${({ isOpen }) => (isOpen ? openProps : "")}

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const overlayActiveProps = css`
  opacity: 0.1;
  transition: opacity ${animation.medium} ${cubicBezier};
`

const Overlay = styled.div<{ isVisible: boolean }>`
  z-index: 3;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  will-change: opacity;
  overflow: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  transition: opacity ${animation.kindaLong} ${cubicBezier};
  width: 100%;
  height: 100%;
  background-color: ${colors.black};
  pointer-events: none;

  ${({ isVisible }) => (isVisible ? overlayActiveProps : "")}

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const Content = styled.div`
  width: 100%;
  height: 100%;
  padding: 100px 20px 0;

  @media ${breakpoints.tablet} {
    padding: 100px 32px 0;
  }
`

const showErrorProps = css`
  opacity: 1;
  transform: translateY(0);
`

const Error = styled.div<{ showError?: boolean }>`
  z-index: -1;
  position: absolute;
  top: 72px;
  left: 0px;
  justify-content: center;
  align-items: center;
  display: flex;
  opacity: 0;
  transform: translateY(-32px);
  transition: opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
  width: 100%;
  height: 48px;
  background-color: ${colors.errorBackground};

  ${({ showError }) => (showError ? showErrorProps : "")}
`

const Text = styled.div`
  ${smallcapsBold13Styles}
  padding-top: 2px;
  color: ${colors.white};
`

const Button = styled(StyledButton)`
  justify-content: center;
  margin-top: 32px;
  padding: 0;
`

interface AddDiscountProps {
  containerRef: any
  isOpen: boolean
  onAddDiscount: (discount: string | undefined) => DiscountResponseProps
  onAddDiscountSuccess: () => void
  onRequestClose: () => void
}

const AddDiscount: React.FC<AddDiscountProps> = ({
  containerRef,
  isOpen,
  onAddDiscount,
  onAddDiscountSuccess,
  onRequestClose,
}) => {
  const [discount, setDiscount] = React.useState<string>("")
  const [errorMessage, setErrorMessage] = React.useState("")
  const [showError, setShowError] = React.useState(false)

  React.useEffect(() => {
    if (showError) {
      setTimeout(() => {
        setShowError(false)
      }, 3000)
    }
  }, [showError])

  const handleAddDiscount = async () => {
    const response: DiscountResponseProps = await onAddDiscount(discount)

    if (
      (response && response.code === "DISCOUNT_NOT_FOUND") ||
      discount === ""
    ) {
      setDiscount("")
      setErrorMessage("Oops, that's not a valid code")
      setShowError(true)
    } else if (response && response.message) {
      setDiscount("")
      setErrorMessage(response.message)
      setShowError(true)
    } else {
      onAddDiscountSuccess()
      setDiscount("")
    }
  }

  const onChange = (e) => {
    setDiscount(e.target.value)
  }

  const onKeyPress = (e) => {
    if (e.key === "Enter") {
      handleAddDiscount()
    }
  }

  return (
    <>
      <Overlay isVisible={isOpen} />
      <Container ref={containerRef} isOpen={isOpen}>
        <BackButton text="Add Discount Code" onRequestClose={onRequestClose} />
        <Content>
          <TextInput
            value={discount}
            placeholder={"Discount Code"}
            onChange={onChange}
            onKeyPress={onKeyPress}
          />
          <Button onClick={handleAddDiscount}>Add</Button>
        </Content>
        <Error showError={showError}>
          <Text>{errorMessage}</Text>
        </Error>
      </Container>
    </>
  )
}

export default AddDiscount
